import { DocumentReference, onSnapshot } from 'firebase/firestore'

const refsToPaths = (value) => {
  if (value instanceof DocumentReference) return value.path
  if (Array.isArray(value)) return value.map(refsToPaths)
  if (value && typeof value === 'object' && value.constructor === Object) {
    const out = {}
    for (const key in value) out[key] = refsToPaths(value[key])
    return out
  }
  return value
}

export const serializeSnapshot = (snap) =>
  Object.defineProperty(refsToPaths(snap.data() || {}), 'id', { value: snap.id })

const serialize = serializeSnapshot

const isDocumentRef = (ref) => ref && ref.type === 'document'

const describe = (ref) => ref.path || 'query'

function bindDocument(vm, key, ref, resolve, reject) {
  return onSnapshot(
    ref,
    (snap) => {
      vm[key] = snap.exists() ? serialize(snap) : null
      resolve(vm[key])
    },
    (error) => {
      console.error(`$live('${key}', ${describe(ref)}) failed:`, error)
      reject(error)
    }
  )
}

function bindCollection(vm, key, ref, resolve, reject) {
  vm[key] = []

  return onSnapshot(
    ref,
    (snap) => {
      const array = vm[key]

      snap.docChanges().forEach((change) => {
        if (change.type === 'added') {
          array.splice(change.newIndex, 0, serialize(change.doc))
        } else if (change.type === 'modified') {
          array.splice(change.oldIndex, 1)
          array.splice(change.newIndex, 0, serialize(change.doc))
        } else if (change.type === 'removed') {
          array.splice(change.oldIndex, 1)
        }
      })
      resolve(array)
    },
    (error) => {
      console.error(`$live('${key}', ${describe(ref)}) failed:`, error)
      reject(error)
    }
  )
}

export default {
  install(app) {
    app.config.globalProperties.$live = function (key, ref) {
      if (!this._liveUnsubs || this._liveUnmounting) {
        return Promise.resolve(null)
      }

      if (this._liveUnsubs[key]) this.$unlive(key)

      const isDoc = isDocumentRef(ref)

      return new Promise((resolve, reject) => {
        const unsub = isDoc
          ? bindDocument(this, key, ref, resolve, reject)
          : bindCollection(this, key, ref, resolve, reject)

        this._liveUnsubs[key] = { unsub, isDoc }
      })
    }

    // Stops syncing and resets the target, matching VueFire's reset: true default.
    app.config.globalProperties.$unlive = function (key, reset = true) {
      const bound = this._liveUnsubs && this._liveUnsubs[key]
      if (!bound) return

      bound.unsub()
      delete this._liveUnsubs[key]

      if (reset) this[key] = bound.isDoc ? null : []
    }

    app.mixin({
      beforeCreate() {
        this._liveUnsubs = Object.create(null)
        this._liveUnmounting = false
      },
      beforeUnmount() {
        // No reset here; the component is going away.
        this._liveUnmounting = true
        for (const key in this._liveUnsubs) this._liveUnsubs[key].unsub()
        this._liveUnsubs = null
      }
    })
  }
}
